import React, { useEffect, useState } from 'react';
import { post } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { getLastLogin } from '../lib/lastLogin';

export default function MobileSignIn({ onSignedIn, onBusyChange, disabled = false }) {
  const { loginWithMobile } = useAuth();
  const [phone, setPhone] = useState(() => { const last = getLastLogin(); return last?.method === 'PHONE' ? last.phone || '' : ''; });
  const [channel, setChannel] = useState('phone');
  const [maskedDestination, setMaskedDestination] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [code, setCode] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { onBusyChange?.(busy); }, [busy, onBusyChange]);
  useEffect(() => {
    if (!seconds) return undefined;
    const timer = setTimeout(() => setSeconds(seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);
  const request = async event => {
    event?.preventDefault(); setBusy(true); setError('');
    try {
      const response = await post('/auth/mobile/request', { phone, channel: 'phone', ...(process.env.REACT_APP_PORTAL_ROLE === 'VENDOR' ? { portal: 'VENDOR' } : {}) });
      setSentTo(response.destination); setPhone(response.destination); setCode('');
      setChannel(response.channel || 'phone'); setMaskedDestination(response.maskedDestination || response.destination);
      setSeconds(response.retryAfter || 60);
    } catch (e) { setError(e.response?.data?.message || e.message || 'Unable to send a code.'); }
    finally { setBusy(false); }
  };
  const confirm = async event => {
    event.preventDefault(); setBusy(true); setError('');
    try { onSignedIn(await loginWithMobile(sentTo, code, channel)); }
    catch (e) { setError(e.response?.data?.message || e.message || 'Unable to verify the code.'); }
    finally { setBusy(false); }
  };
  return <div className="space-y-4">
    {getLastLogin()?.method === 'PHONE' && <p className="text-xs font-semibold text-primary">Last used</p>}
    <form onSubmit={sentTo ? confirm : request} className="space-y-4">
      <label className="block text-sm font-medium">PH mobile number
        <input type="tel" autoComplete="tel" inputMode="tel" placeholder="09171234567" required
          value={phone} disabled={busy || disabled || Boolean(sentTo)}
          onChange={e => setPhone(e.target.value)} className="mt-2 w-full rounded-xl border p-3 disabled:bg-gray-50" />
      </label>
      {!sentTo && <p className="text-xs text-gray-500">{process.env.REACT_APP_PORTAL_ROLE === 'VENDOR' ? 'Enter the mobile number linked to your existing account to receive a sign-in code. Vendor registration is not available here.' : 'Enter your mobile number to receive an SMS code. Verify it to sign in or create an account.'}</p>}
      {sentTo && <>
        <p role="status" className="text-sm text-gray-600">{channel === 'email' ? 'To use phone sign-in for your Google account, enter the email code sent to' : 'Enter the SMS code sent to'} {maskedDestination}. The code expires in 5 minutes.</p>
        <label className="block text-sm font-medium">Verification code
          <input required value={code} disabled={busy || disabled} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
            className="mt-2 w-full rounded-xl border p-3 text-center text-xl tracking-widest" />
        </label>
      </>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <Button type="submit" className="w-full" disabled={busy || disabled || (sentTo ? code.length !== 6 : !phone.trim() || seconds > 0)}>
        {busy ? 'Please wait...' : sentTo ? 'Verify' : seconds ? `Send again in ${seconds}s` : 'Send verification code'}
      </Button>
      {sentTo && <div className="flex justify-between gap-3">
        <Button type="button" variant="ghost" disabled={busy || disabled} onClick={() => { setSentTo(''); setCode(''); setError(''); }}>Change number</Button>
        <Button type="button" variant="ghost" disabled={busy || disabled || seconds > 0} onClick={request}>{seconds ? `Resend in ${seconds}s` : 'Resend code'}</Button>
      </div>}
    </form>
  </div>;
}
